import { useCallback, useRef, useState } from 'react';
import {
  createPrintOrder,
  failPrintOrder,
  printOrder,
} from '../services/printOrdersAPI';
import { getApiErrorMessage } from '../services/errorUtils';
import {
  awaitPrintedAck,
  PRINT_ACK_MAX_ATTEMPTS,
  PRINT_ACK_TIMEOUT_MS,
  PrintAckFailedError,
} from '../utils/printAckLoop';
import type { PrintOrderWithSheetsResponse } from '../schemas/apiResponse.schemas';
import type { CreatePrintOrderInput } from '@/shared/types/printOrder.types';
import { DEFAULT_PRINTER_ID, type DestinationPrinterId } from '@/shared/types/printer.types';

export interface PrintRunProgress {
  readonly attempt: number;
  readonly maxAttempts: number;
}

export function printRunProgressMessage(progress: PrintRunProgress): string {
  return progress.attempt === 1
    ? 'Sending print to printer...'
    : `Retrying print (attempt ${progress.attempt}/${progress.maxAttempts})...`;
}

export interface PrintRunResult {
  readonly orderId: string | null;
  readonly totalLabels: number;
  readonly printedLabels: number;
  readonly completed: boolean;
  readonly message: string;
  readonly order: PrintOrderWithSheetsResponse | null;
}

export interface PrintRunOptions {
  readonly onProgress?: (progress: PrintRunProgress) => void;
  /** Noun used in the result message, pluralised with a trailing "s". Defaults to "label". */
  readonly labelNoun?: string;
  /** Destination printer for this run. Defaults to Shipping Computer. */
  readonly printerId?: DestinationPrinterId;
}

export interface UsePrintOrderRun {
  /** Create an order, then send its queue as one relay job. */
  readonly runPrintOrder: (input: CreatePrintOrderInput, options?: PrintRunOptions) => Promise<PrintRunResult>;
  /** Print an order that already exists (a balance retry). */
  readonly runOrder: (order: PrintOrderWithSheetsResponse, options?: PrintRunOptions) => Promise<PrintRunResult>;
  readonly isRunning: boolean;
}

/**
 * Drives a print order end to end: one relay request for the whole queue. A job
 * that never ACKs after {@link PRINT_ACK_MAX_ATTEMPTS} attempts fails the order,
 * which cancels its remaining labels and leaves a balance recoverable from the
 * Print Log list.
 */
export function usePrintOrderRun(): UsePrintOrderRun {
  const [isRunning, setIsRunning] = useState(false);
  const runningRef = useRef(false);

  const runOrder = useCallback(
    async (order: PrintOrderWithSheetsResponse, options?: PrintRunOptions): Promise<PrintRunResult> => {
      const { order: created } = order;
      const totalLabels = created.totalLabels;
      const noun = options?.labelNoun ?? 'label';
      const printerId = options?.printerId ?? DEFAULT_PRINTER_ID;

      runningRef.current = true;
      setIsRunning(true);

      try {
        await awaitPrintedAck(() => printOrder(created.id, printerId), {
          maxAttempts: PRINT_ACK_MAX_ATTEMPTS,
          timeoutMs: PRINT_ACK_TIMEOUT_MS,
          onAttempt: ({ attempt, maxAttempts }) => {
            options?.onProgress?.({ attempt, maxAttempts });
          },
        });

        return {
          orderId: created.id,
          totalLabels,
          printedLabels: totalLabels,
          completed: true,
          message:
            totalLabels === 1
              ? `Printed 1 ${noun} successfully!`
              : `Printed ${totalLabels} ${noun}s.`,
          order,
        };
      } catch (error: unknown) {
        const isAckFailure = error instanceof PrintAckFailedError;
        const reason = isAckFailure
          ? error.message
          : getApiErrorMessage(error, 'Print failed.');
        await failPrintOrder(created.id, isAckFailure ? 'ack_exhausted' : 'relay_error', reason);
        return {
          orderId: created.id,
          totalLabels,
          printedLabels: 0,
          completed: false,
          message: `Stopped after 0 of ${totalLabels} ${noun}${totalLabels !== 1 ? 's' : ''}. ${totalLabels} left to print — retry from Print Log.`,
          order,
        };
      } finally {
        runningRef.current = false;
        setIsRunning(false);
      }
    },
    [],
  );

  const runPrintOrder = useCallback(
    async (input: CreatePrintOrderInput, options?: PrintRunOptions): Promise<PrintRunResult> => {
      if (runningRef.current) {
        return {
          orderId: null,
          totalLabels: 0,
          printedLabels: 0,
          completed: false,
          message: 'A print is already running.',
          order: null,
        };
      }
      let order: PrintOrderWithSheetsResponse;
      try {
        order = await createPrintOrder(input);
      } catch (error: unknown) {
        console.error('Error creating print order:', error);
        return {
          orderId: null,
          totalLabels: 0,
          printedLabels: 0,
          completed: false,
          message: getApiErrorMessage(error, 'Failed to start print order.'),
          order: null,
        };
      }
      return runOrder(order, options);
    },
    [runOrder],
  );

  return { runPrintOrder, runOrder, isRunning };
}
